import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Menu, {
  findActiveSectionId,
  isPathActive,
} from "../../config/app-menu.jsx";
import { AppSettings } from "../../config/app-settings.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import SidebarMenuItem from "./SidebarMenuItem.jsx";
import SidebarFloatSubmenu from "./SidebarFloatSubmenu.jsx";
import "./sidebar-nav.css";

function accentVars(hex) {
  const base = hex || "#30D6A4";
  return {
    "--sidebar-accent": base,
    "--sidebar-accent-soft": `${base}24`,
    "--sidebar-accent-softer": `${base}14`,
    "--sidebar-accent-mid": `${base}38`,
    "--sidebar-accent-shadow": `${base}40`,
  };
}

function SidebarNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const themeColor = useThemeColor();
  const accentStyle = useMemo(() => accentVars(themeColor), [themeColor]);
  const { appSidebarMinify, toggleAppSidebarMobile } = useContext(AppSettings);

  const [expandedId, setExpandedId] = useState(() =>
    findActiveSectionId(pathname, Menu)
  );
  const [floatMenu, setFloatMenu] = useState(null);
  const floatCloseTimer = useRef(null);
  const floatAnchorId = useRef(null);

  useEffect(() => {
    setExpandedId(findActiveSectionId(pathname, Menu));
  }, [pathname]);

  useEffect(() => {
    if (!appSidebarMinify) {
      clearTimeout(floatCloseTimer.current);
      setFloatMenu(null);
      floatAnchorId.current = null;
    }
  }, [appSidebarMinify]);

  const dismissFloat = useCallback(() => {
    clearTimeout(floatCloseTimer.current);
    setFloatMenu(null);
    floatAnchorId.current = null;
  }, []);

  const closeFloat = useCallback(() => {
    clearTimeout(floatCloseTimer.current);
    floatCloseTimer.current = setTimeout(() => {
      setFloatMenu(null);
      floatAnchorId.current = null;
    }, 250);
  }, []);

  const cancelCloseFloat = useCallback(() => {
    clearTimeout(floatCloseTimer.current);
  }, []);

  const openFloat = useCallback(
    (item, event) => {
      if (!appSidebarMinify || !item.children?.length) return;
      const anchor = event?.currentTarget;
      if (!anchor) return;
      cancelCloseFloat();
      if (floatAnchorId.current === item.id && floatMenu) return;

      floatAnchorId.current = item.id;
      const sidebar = document.querySelector("#sidebar");
      const sidebarOffset = sidebar?.getBoundingClientRect();
      const sidebarWidth = sidebar?.clientWidth ?? 0;
      const bodyStyle = getComputedStyle(document.body);
      const appElm = document.querySelector(".app");
      const isRtl = bodyStyle.getPropertyValue("direction") === "rtl";
      const isEnd = appElm?.classList.contains("app-sidebar-end");
      const left =
        !isEnd && !isRtl
          ? (sidebarOffset?.left ?? 0) + sidebarWidth
          : document.body.clientWidth - (sidebarOffset?.left ?? 0);

      setFloatMenu({
        item,
        anchorRect: anchor.getBoundingClientRect(),
        sidebarLeft: left,
        dark: sidebar?.getAttribute("data-bs-theme") === "dark",
      });
    },
    [appSidebarMinify, cancelCloseFloat, floatMenu]
  );

  const toggleSection = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleNavigate = useCallback(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      toggleAppSidebarMobile?.({ preventDefault() {} });
    }
  }, [toggleAppSidebarMobile]);

  return (
    <div className="menu mt-0 sidebar-nav-menu" style={accentStyle}>
      {Menu.map((item) => {
        if (!item.children?.length) {
          const active = isPathActive(pathname, item.match || item.path);
          return (
            <div
              key={item.id}
              className={`menu-item${active ? " active" : ""}`}
            >
              <NavLink
                to={item.path}
                className="menu-link"
                end={item.path === "/dashboard"}
                onClick={handleNavigate}
              >
                <div className="menu-icon">
                  <i className={item.icon} />
                </div>
                <div className="menu-text">{item.title}</div>
              </NavLink>
            </div>
          );
        }

        const sectionActive = isPathActive(pathname, item.match || item.path);
        const isExpanded = expandedId === item.id;

        return (
          <div
            key={item.id}
            className={`menu-item has-sub${sectionActive ? " active" : ""}${
              isExpanded ? " expand" : " closed"
            }`}
            onMouseEnter={(e) => openFloat(item, e)}
            onMouseLeave={closeFloat}
          >
            <button
              type="button"
              className="menu-link"
              aria-expanded={isExpanded}
              aria-controls={`sidebar-submenu-${item.id}`}
              id={`sidebar-section-${item.id}`}
              onClick={() => {
                if (appSidebarMinify) {
                  openFloat(item, {
                    currentTarget: document.getElementById(
                      `sidebar-section-${item.id}`
                    ),
                  });
                  return;
                }
                toggleSection(item.id);
              }}
              onFocus={(e) => openFloat(item, e)}
            >
              <div className="menu-icon">
                <i className={item.icon} />
              </div>
              <div className="menu-text">{item.title}</div>
              <div className="menu-caret" aria-hidden="true" />
            </button>
            <div
              className="menu-submenu"
              id={`sidebar-submenu-${item.id}`}
              role="region"
              aria-labelledby={`sidebar-section-${item.id}`}
              hidden={!isExpanded}
            >
              {item.children.map((child) => (
                <SidebarMenuItem
                  key={child.path}
                  item={child}
                  pathname={pathname}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          </div>
        );
      })}

      {floatMenu ? (
        <SidebarFloatSubmenu
          item={floatMenu.item}
          pathname={pathname}
          anchorRect={floatMenu.anchorRect}
          sidebarLeft={floatMenu.sidebarLeft}
          dark={floatMenu.dark}
          accent={themeColor || "#30D6A4"}
          onKeepOpen={cancelCloseFloat}
          onRequestClose={closeFloat}
          onDismiss={dismissFloat}
          onNavigate={handleNavigate}
        />
      ) : null}
    </div>
  );
}

export default SidebarNav;
